import { LoadOptions } from 'react-select-async-paginate';

/**
 * Creates an async loader function for react-select-async-paginate
 * This helps standardize the pattern for loading paginated options
 *
 * @template T - The type of items returned by the fetch function
 * @template R - The type of returned option objects (default: { label: string; value: string | number })
 * @param fetchFn - Function that fetches data, receives search and page parameters
 * @param mapFn - Function that maps each fetched item to an option object with label and value
 * @returns An async function compatible with react-select-async-paginate's loadOptions prop
 *
 * @example
 * ```ts
 * const loadFarmOptions = useMemo(
 *   () => createAsyncOptionLoader<IFarm>(
 *     ({ search, page }) => uiStore.currentOrganization!.fetchFarms({ search, page }),
 *     (farm) => ({ label: farm.name, value: farm.id })
 *   ),
 *   []
 * )
 * ```
 *
 * @remarks
 * This function handles the pagination logic for react-select-async-paginate.
 * It returns an async function that fetches data and formats it as required by the component.
 * The returned function is meant to be used as the `loadOptions` prop in LFAsyncSelect components.
 */
export const createAsyncOptionLoader = <T, R = {
  label: string;
  value: string | number;
}>(fetchFn: (params: {
  search?: string;
  page: number;
}) => Promise<T[]>, mapFn: (item: T) => R): LoadOptions<R, any, {
  page: number;
}> => async (search: string = '', _prevOptions: {
  [key: string]: any;
}[], {
  page
}: {
  page: number;
}) => {
  const response = (await fetchFn({
    search,
    page
  })) || [];
  const hasMore = response.length > 0;
  return {
    options: response.map(mapFn),
    hasMore,
    additional: {
      page: page + 1
    }
  };
};